import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../api'

export default function Settings() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const [exportLoading, setExportLoading] = useState(false)
  const [exportSuccess, setExportSuccess] = useState(false)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  // Check if delete is allowed
  const canDelete = deleteConfirmText.trim().toUpperCase() === 'DELETE'
    && deletePassword.length > 0
    && !deleteLoading

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (passwordLoading) return

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      return
    }

    setPasswordError('')
    setPasswordSuccess('')
    setPasswordLoading(true)

    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      setPasswordSuccess('Password updated successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 429) {
        setPasswordError('Too many attempts. Please try again later.')
      } else {
        setPasswordError(err?.response?.data?.error || 'Failed to update password')
      }
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleExportData = async () => {
    if (exportLoading) return
    setExportLoading(true)
    setExportSuccess(false)

    try {
      const token = localStorage.getItem('velumio_token')
      const response = await fetch(
        (process.env.REACT_APP_API_URL || 'http://localhost:3000') + '/auth/export-data',
        {
          method: 'GET',
          headers: { Authorization: 'Bearer ' + token },
        }
      )

      if (!response.ok) {
        throw new Error('Export failed')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'velumio-data-export-' + new Date().toISOString().split('T')[0] + '.json'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setExportSuccess(true)
      setTimeout(() => setExportSuccess(false), 4000)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setExportLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    console.log('Delete clicked. Text:', deleteConfirmText, 'Password length:', deletePassword.length)

    if (deleteLoading) return

    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm')
      return
    }

    if (!deletePassword) {
      setDeleteError('Password is required')
      return
    }

    setDeleteError('')
    setDeleteLoading(true)

    try {
      console.log('Sending delete request...')
      await api.delete('/auth/account', { data: { password: deletePassword } })
      console.log('Account deleted successfully')
      logout()
      navigate('/')
    } catch (err: any) {
      console.error('Delete failed:', err)
      setDeleteError(err?.response?.data?.error || 'Failed to delete account')
    } finally {
      setDeleteLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', fontSize: '14px',
    border: '1.5px solid var(--warm-border)', borderRadius: 'var(--radius-md)',
    outline: 'none', fontFamily: 'var(--font-body)', color: 'var(--ink)',
    background: 'var(--cream)', boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', fontWeight: 600,
    color: 'var(--ink)', marginBottom: '8px',
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--white)', borderRadius: 'var(--radius-lg)',
    padding: '32px', border: '1px solid var(--cream-dark)',
    marginBottom: '24px',
  }

  return (
    <div className="animate-fadeUp">

      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '4px' }}>
        Settings
      </h2>
      <p style={{ color: 'var(--muted)', marginBottom: '32px' }}>
        Manage your account and preferences.
      </p>

      {/* ACCOUNT INFO */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--navy)', marginBottom: '20px' }}>
          Account
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px' }}>
          <div>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Name</p>
            <p style={{ fontSize: '15px', color: 'var(--ink)', fontWeight: 500 }}>{user?.display_name}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Email</p>
            <p style={{ fontSize: '15px', color: 'var(--ink)', fontWeight: 500 }}>{user?.email}</p>
          </div>
          <div>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>Plan</p>
            <p style={{ fontSize: '15px', color: 'var(--ink)', fontWeight: 500, textTransform: 'capitalize' as const }}>
              {user?.plan_tier || 'Free'}
            </p>
          </div>
        </div>
      </div>

      {/* CHANGE PASSWORD */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--navy)', marginBottom: '20px' }}>
          Change Password
        </h3>
        <form onSubmit={handleChangePassword}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Your current password"
              required
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              style={inputStyle}
            />
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat your new password"
              required
              style={inputStyle}
            />
          </div>

          {passwordError && (
            <div style={{
              padding: '12px 14px', background: 'var(--rose-light)',
              borderRadius: 'var(--radius-md)', fontSize: '13px',
              color: 'var(--rose)', marginBottom: '16px',
            }}>
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div style={{
              padding: '12px 14px', background: 'rgba(45,106,79,0.1)',
              borderRadius: 'var(--radius-md)', fontSize: '13px',
              color: 'var(--green)', marginBottom: '16px',
            }}>
              {passwordSuccess}
            </div>
          )}

          <button
            type="submit"
            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
            style={{
              padding: '12px 28px', fontSize: '14px', fontWeight: 600,
              color: 'var(--white)',
              background: passwordLoading || !currentPassword || !newPassword || !confirmPassword
                ? 'var(--muted)' : 'var(--navy)',
              border: 'none', borderRadius: 'var(--radius-md)',
              cursor: passwordLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >
            {passwordLoading ? 'Updating...' : 'Update password'}
          </button>
        </form>
      </div>

      {/* DATA EXPORT */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--navy)', marginBottom: '8px' }}>
          Export Your Data
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '8px', lineHeight: 1.6 }}>
          Download a complete copy of all your personal data including journal entries,
          wellbeing scores, profile information, and activity data.
        </p>
        <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '20px', fontStyle: 'italic' }}>
          Your right to data portability under UK GDPR Article 20.
        </p>

        {exportSuccess && (
          <div style={{
            padding: '12px 14px', background: 'rgba(45,106,79,0.1)',
            borderRadius: 'var(--radius-md)', fontSize: '13px',
            color: 'var(--green)', marginBottom: '16px',
          }}>
            Your data export has been downloaded successfully.
          </div>
        )}

        <button
          onClick={handleExportData}
          disabled={exportLoading}
          style={{
            padding: '12px 28px', fontSize: '14px', fontWeight: 600,
            color: 'var(--navy)',
            background: exportLoading ? 'var(--cream-dark)' : 'var(--cream)',
            border: '1.5px solid var(--warm-border)',
            borderRadius: 'var(--radius-md)',
            cursor: exportLoading ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          {exportLoading ? 'Preparing export...' : 'Download my data'}
        </button>
      </div>

      {/* LEGAL */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--navy)', marginBottom: '20px' }}>
          Legal
        </h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' as const }}>
          <button
            onClick={() => navigate('/privacy')}
            style={{
              padding: '10px 20px', fontSize: '14px',
              background: 'transparent', border: '1.5px solid var(--warm-border)',
              borderRadius: 'var(--radius-md)', cursor: 'pointer',
              color: 'var(--ink-soft)', fontFamily: 'var(--font-body)',
            }}
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/terms')}
            style={{
              padding: '10px 20px', fontSize: '14px',
              background: 'transparent', border: '1.5px solid var(--warm-border)',
              borderRadius: 'var(--radius-md)', cursor: 'pointer',
              color: 'var(--ink-soft)', fontFamily: 'var(--font-body)',
            }}
          >
            Terms of Service
          </button>
        </div>
      </div>

      {/* SIGN OUT */}
      <div style={cardStyle}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--navy)', marginBottom: '8px' }}>
          Sign out
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px' }}>
          Sign out of your Velumio account on this device.
        </p>
        <button
          onClick={() => { logout(); navigate('/') }}
          style={{
            padding: '12px 28px', fontSize: '14px', fontWeight: 600,
            color: 'var(--navy)', background: 'transparent',
            border: '1.5px solid var(--navy)', borderRadius: 'var(--radius-md)',
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >
          Sign out
        </button>
      </div>

      {/* DELETE ACCOUNT */}
      <div style={{
        ...cardStyle,
        border: '1px solid rgba(192,57,43,0.2)',
        background: 'rgba(192,57,43,0.02)',
      }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: '#C0392B', marginBottom: '8px' }}>
          Delete Account
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '20px', lineHeight: 1.6 }}>
          Permanently delete your account and all associated data including journal entries,
          wellbeing scores, and profile information. This action cannot be undone.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          style={{
            padding: '12px 28px', fontSize: '14px', fontWeight: 600,
            color: '#C0392B', background: 'transparent',
            border: '1.5px solid #C0392B', borderRadius: 'var(--radius-md)',
            cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >
          Delete my account
        </button>
      </div>

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15,27,45,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}>
          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-lg)',
            padding: '40px', maxWidth: '460px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            maxHeight: '90vh', overflowY: 'auto' as const,
          }}>
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: '22px',
              color: '#C0392B', marginBottom: '12px',
            }}>
              Delete your account?
            </h3>
            <p style={{
              fontSize: '14px', color: 'var(--muted)',
              lineHeight: 1.6, marginBottom: '24px',
            }}>
              This will permanently delete your account and all your data including every journal
              entry, wellbeing score, and profile setting. This cannot be undone.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                Type DELETE to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => {
                  setDeleteConfirmText(e.target.value)
                  setDeleteError('')
                }}
                placeholder="DELETE"
                autoComplete="off"
                style={{
                  ...inputStyle,
                  borderColor: deleteConfirmText.trim().toUpperCase() === 'DELETE'
                    ? '#27AE60' : 'var(--warm-border)',
                }}
              />
              {deleteConfirmText.length > 0 && deleteConfirmText.trim().toUpperCase() !== 'DELETE' && (
                <p style={{ fontSize: '11px', color: '#C0392B', marginTop: '6px' }}>
                  Must type exactly: DELETE
                </p>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={e => {
                  setDeletePassword(e.target.value)
                  setDeleteError('')
                }}
                placeholder="Your current password"
                autoComplete="current-password"
                style={inputStyle}
              />
            </div>

            {deleteError && (
              <div style={{
                padding: '12px 14px', background: 'var(--rose-light)',
                borderRadius: 'var(--radius-md)', fontSize: '13px',
                color: 'var(--rose)', marginBottom: '16px',
              }}>
                {deleteError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletePassword('')
                  setDeleteConfirmText('')
                  setDeleteError('')
                }}
                style={{
                  flex: 1, padding: '12px', fontSize: '14px',
                  color: 'var(--ink-soft)', background: 'transparent',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={!canDelete}
                style={{
                  flex: 1, padding: '12px', fontSize: '14px', fontWeight: 600,
                  color: 'var(--white)',
                  background: canDelete ? '#C0392B' : 'var(--muted)',
                  border: 'none', borderRadius: 'var(--radius-md)',
                  cursor: canDelete ? 'pointer' : 'not-allowed',
                  fontFamily: 'var(--font-body)',
                  opacity: canDelete ? 1 : 0.6,
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete forever'}
              </button>
            </div>

            <p style={{
              fontSize: '11px', color: 'var(--muted)',
              textAlign: 'center', marginTop: '16px',
            }}>
              Tip: you may want to export your data first before deleting.
            </p>
          </div>
        </div>
      )}

    </div>
  )
}